/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable, Optional} from '@angular/core';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import * as _ from 'lodash';
import {map, Observable} from 'rxjs';

import {ResourceHelper} from '../../../components/util/resource-helper';
import {AdnApiClientService} from '../../common/controller/adn-api-client.service';
import {
  SignalAdminResourceApiBase,
  SignalAdminResourceListParams,
  SignalAdminResourceModel,
  SignalAdminResourcePage
} from '../../common/controller/signal-admin-resource-api';

export interface ProductModel extends SignalAdminResourceModel {
  [key: string]: any;
}

function transformProductRequest(product: ProductModel): ProductModel {
  const transformed = _.cloneDeep(product);
  ['minimumBudget', 'maximumBudget', 'minimumCpm'].forEach((fieldName) => {
    ResourceHelper.checkMoney(transformed, [fieldName]);
  });
  if (_.isPlainObject(transformed.data)) {
    transformed.data = _.mapValues(transformed.data, (value: any) => _.isObject(value) ? JSON.stringify(value) : value);
  }
  return transformed;
}

function transformProductResponse(product: ProductModel): ProductModel {
  if (_.isPlainObject(product?.data)) {
    product.data = _.mapValues(product.data, (value: any) => {
      if (!_.isString(value)) {
        return value;
      }
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    });
  }
  return product;
}

@Injectable({providedIn: 'root'})
export class ProductApiService extends SignalAdminResourceApiBase<ProductModel> {
  constructor(client: AdnApiClientService, @Optional() localUserProfile?: LocalUserProfile) {
    super(client, 'product', {}, () => localUserProfile?.isReadOnly() || false);
  }

  override get(id: string): Observable<ProductModel> {
    return super.get(id).pipe(map(transformProductResponse));
  }

  override list(params: SignalAdminResourceListParams): Observable<SignalAdminResourcePage<ProductModel>> {
    return super.list(params).pipe(map((page) => ({
      ...page,
      results: (page.results || []).map(transformProductResponse)
    })));
  }

  listOptions(params: Record<string, any> = {}): Observable<SignalAdminResourcePage<ProductModel>> {
    return this.client.request<SignalAdminResourcePage<ProductModel>>({
      method: 'GET',
      url: this.resourceUri('product'),
      params
    }).pipe(map((page) => ({...page, results: (page.results || []).map(transformProductResponse)})));
  }

  override save(model: ProductModel): Observable<ProductModel> {
    return super.save(transformProductRequest(model)).pipe(map(transformProductResponse));
  }
}
